import React, { useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Tag } from 'antd';
import {
  HomeOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MailOutlined,
  CrownOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout, setUser } from '../store/slices/authSlice';
import './SimpleAdminLayout.css';

const { Header, Content } = Layout;

const SimpleAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Initialize user from localStorage on mount
  useEffect(() => {
    if (!user && isAuthenticated) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const token = localStorage.getItem('token');
          dispatch(setUser({ user: userData, token, isAuthenticated: true }));
        } catch (error) {
          console.error('Failed to parse stored user:', error);
        }
      }
    }
  }, [user, isAuthenticated, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const userMenu = {
    items: [
      {
        key: 'profile-header',
        label: (
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
              {user?.name || 'User'}
            </div>
            <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <MailOutlined style={{ fontSize: '11px' }} />
              {user?.email || 'No email'}
            </div>
            {(user?.role === 'admin' || user?.is_admin) && (
              <Tag color="blue" icon={<CrownOutlined />} style={{ marginTop: '8px', fontSize: '11px' }}>
                Administrator
              </Tag>
            )}
            {user?.role === 'verifier' && (
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginTop: '8px', fontSize: '11px' }}>
                Verifier
              </Tag>
            )}
          </div>
        ),
        disabled: true,
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: handleLogout,
        danger: true,
      },
    ],
  };

  // Build menu items based on user role
  const userRole = user?.role || (user?.is_admin ? 'admin' : 'user');

  const mainMenuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: 'Leaderboard',
    },
  ];

  // Add admin panel for admins
  if (userRole === 'admin') {
    mainMenuItems.push({
      key: '/admin',
      icon: <SettingOutlined />,
      label: 'Admin Panel',
    });
  }

  // Add verificator panel for verifiers
  if (userRole === 'verifier') {
    mainMenuItems.push({
      key: '/verificator',
      icon: <CheckCircleOutlined />,
      label: 'Verificator Panel',
    });
  }

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  return (
    <Layout className="simple-admin-layout">
      <Header className="simple-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-text">GAMIFIKASI</div>
            <div className="logo-subtitle">#AI-ITERA</div>
          </div>

          <Menu
            mode="horizontal"
            selectedKeys={[
              location.pathname.startsWith('/admin') ? '/admin' :
              location.pathname.startsWith('/verificator') ? '/verificator' :
              location.pathname
            ]}
            items={mainMenuItems}
            onClick={handleMenuClick}
            className="main-menu"
          />

          <Space size="large">
            {isAuthenticated ? (
              <Dropdown menu={userMenu} placement="bottomRight">
                <Space style={{ cursor: 'pointer' }} className="user-dropdown">
                  <Avatar
                    src={user?.picture}
                    icon={<UserOutlined />}
                    className="user-avatar"
                  />
                  <span className="user-profile-name">{user?.name || user?.email}</span>
                </Space>
              </Dropdown>
            ) : (
              <Button type="primary" onClick={() => navigate('/login')}>
                Login
              </Button>
            )}
          </Space>
        </div>
      </Header>

      <Content className="simple-content">
        {children}
      </Content>
    </Layout>
  );
};

export default SimpleAdminLayout;
